/**
 * Actor Passport — actor-centered authority surface (V3 screen 12).
 *
 * A READ projection of an actor's full authority footprint, assembled by the
 * worker into one `ActorPassportPacket`
 * (`GET /api/actors/:actorId/passport`, `apps/worker/src/routes/actor-authority.ts`):
 * identities, memberships, role paths, delegation grants, credential grants,
 * action-policy overrides, the resolved runtime principal, and any integrity
 * warnings the assembler raised.
 *
 * Doctrine (mirrors DecisionRoom + the shared inspector grammar):
 *   • Read-only. Nothing here mutates business truth — actions that *would*
 *     change authority (grant/revoke/delegate) live on the server-enforced
 *     endpoints; this surface only links out to them.
 *   • The body routes through the shared eight-state contract
 *     (`components/app/RenderState`): loading / empty / normal / stale /
 *     overflow / redacted / forbidden / error. The passport picks its state
 *     from HTTP status (403 → forbidden) and packet content (integrity
 *     warnings → redacted banner; `surface_state` → empty when the actor has
 *     no identity / no authority).
 *   • shadcn-only visuals; every icon via `components/ui/icon` (HugeIcons keys).
 *   • Secrets never reach the client — the packet exposes scope hashes and
 *     grant ids, never secret refs (enforced worker-side; see the topic-10
 *     integration suite).
 */
import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type { ActorPassportPacket } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Icon } from "@/components/ui/icon";
import { ErrorState } from "@/components/ui/states";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { api, ApiError } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

/* Element types derived from the packet (the shared package exports only the
 * packet alias; indexed access keeps these in lock-step with the schema). */
type Identity = ActorPassportPacket["identities"][number];
type Membership = ActorPassportPacket["memberships"][number];
type RolePath = ActorPassportPacket["role_paths"][number];
type DelegationGrant = ActorPassportPacket["delegation_grants"][number];
type CredentialGrant = ActorPassportPacket["credential_grants"][number];
type PolicyOverride = ActorPassportPacket["policy_overrides"][number];

interface PacketState {
  data: ActorPassportPacket | null;
  loading: boolean;
  error: string | null;
  forbidden: boolean;
  reload: () => void;
}

function useActorPassportPacket(actorId: string): PacketState {
  const [data, setData] = React.useState<ActorPassportPacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [forbidden, setForbidden] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setForbidden(false);
    api
      .get<ActorPassportPacket>(`/actors/${encodeURIComponent(actorId)}/passport`)
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true);
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load actor passport.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [actorId, reloadKey]);

  return { data, loading, error, forbidden, reload };
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/** Compact active/total chip used across the section headers. */
function ActiveOfBadge({ active, total }: { active: number; total: number }) {
  return (
    <Badge variant="outline" className="font-mono">
      {active} active / {total}
    </Badge>
  );
}

function MonoOrDash({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <span className="font-mono text-xs [overflow-wrap:anywhere]">{value}</span>;
}

function ActiveCell({ active }: { active: boolean }) {
  return active ? (
    <StatusBadge status="active" />
  ) : (
    <Badge variant="outline" className="text-muted-foreground">
      inactive
    </Badge>
  );
}

function OverviewSection({ packet }: { packet: ActorPassportPacket }) {
  const actor = packet.actor;
  const principal = packet.runtime_principal;
  return (
    <InspectorSection
      title="Overview"
      aside={
        <span className="text-xs text-muted-foreground">
          Generated {formatDateTime(packet.generated_at)}
        </span>
      }
    >
      <FieldGrid>
        <Field label="Actor">
          <span className="[overflow-wrap:anywhere]">{actor.display_name}</span>
        </Field>
        <Field label="Type">{humanize(actor.actor_type)}</Field>
        <Field label="Class">{humanize(actor.actor_class)}</Field>
        <Field label="Status">
          <StatusBadge status={actor.status} />
        </Field>
        <Field label="Surface state">
          <StatusBadge status={packet.surface_state} />
        </Field>
        <Field label="Default user">
          <RefLink resource="users" id={actor.default_user_id} />
        </Field>
        <Field label="Resolved role">
          {principal.active_role_names.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {principal.active_role_names.map((role) => (
                <Badge key={role} variant="secondary">
                  {humanize(role)}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">No active roles</span>
          )}
        </Field>
        <Field label="Approval mode">
          <StatusBadge status={principal.approval_mode} />
        </Field>
        <Field label="Created">{formatDateTime(actor.created_at)}</Field>
        <Field label="Updated">{formatDateTime(actor.updated_at)}</Field>
        <Field label="Id">
          <span className="font-mono text-xs">{actor.id}</span>
        </Field>
      </FieldGrid>
    </InspectorSection>
  );
}

function RuntimePrincipalSection({ packet }: { packet: ActorPassportPacket }) {
  const p = packet.runtime_principal;
  return (
    <InspectorSection
      title="Runtime Principal"
      aside={
        <Badge variant="outline" className="font-mono">
          {p.principal_fingerprint}
        </Badge>
      }
    >
      <FieldGrid>
        <Field label="Requested capability">
          <MonoOrDash value={p.requested_capability} />
        </Field>
        <Field label="Requested context">
          <MonoOrDash value={p.requested_context_id} />
        </Field>
        <Field label="Requested resource">
          <MonoOrDash value={p.requested_resource_type} />
        </Field>
        <Field label="Risk level">
          <StatusBadge status={p.requested_risk_level} />
        </Field>
        <Field label="On behalf of">
          <RefLink resource="actors" id={p.on_behalf_of_actor_id} />
        </Field>
        <Field label="Authorized via">
          {p.authorized_via ? (
            <span className="font-mono text-xs">
              {humanize(p.authorized_via.path_type)}
            </span>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">
              not authorized
            </Badge>
          )}
        </Field>
        <Field label="Approval mode">
          <StatusBadge status={p.approval_mode} />
        </Field>
        <Field label="Required approvers">{p.required_approver_count}</Field>
        <Field label="Distinct approver">
          {p.requires_distinct_approver ? "Required" : "Not required"}
        </Field>
        <Field label="Credential grants bound">{p.credential_grant_ids.length}</Field>
      </FieldGrid>
      {p.active_capabilities.length > 0 ? (
        <div className="mt-4">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            Active capabilities
          </div>
          <div className="flex flex-wrap gap-1.5">
            {p.active_capabilities.map((cap) => (
              <Badge key={cap} variant="outline" className="font-mono text-xs">
                {cap}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}
    </InspectorSection>
  );
}

function IdentitiesSection({ identities }: { identities: readonly Identity[] }) {
  const active = identities.filter((item) => item.active_now).length;
  return (
    <InspectorSection
      title="Identities"
      aside={<ActiveOfBadge active={active} total={identities.length} />}
    >
      {identities.length === 0 ? (
        <SectionEmpty
          icon="user"
          message="No connector or login identities linked to this actor."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {identities.map((identity) => (
              <TableRow key={identity.id}>
                <TableCell>{humanize(identity.provider)}</TableCell>
                <TableCell>
                  <MonoOrDash value={identity.provider_subject} />
                </TableCell>
                <TableCell>
                  <MonoOrDash value={identity.email} />
                </TableCell>
                <TableCell>{formatDateTime(identity.last_seen_at)}</TableCell>
                <TableCell>
                  <ActiveCell active={identity.active_now} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InspectorSection>
  );
}

function RolePathsSection({ rolePaths }: { rolePaths: readonly RolePath[] }) {
  const active = rolePaths.filter((item) => item.active_now).length;
  return (
    <InspectorSection
      title="Authority Paths"
      aside={<ActiveOfBadge active={active} total={rolePaths.length} />}
    >
      {rolePaths.length === 0 ? (
        <SectionEmpty
          icon="roles"
          message="No role assignments reach this actor — it has no granted authority."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Path</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Context</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rolePaths.map((path) => (
              <TableRow key={path.id}>
                <TableCell>
                  <Badge variant="secondary">{humanize(path.role.name)}</Badge>
                </TableCell>
                <TableCell>{humanize(path.path_type)}</TableCell>
                <TableCell>
                  {path.source_actor_display_name ? (
                    <RefLink
                      resource="actors"
                      id={path.source_actor_id}
                      label={path.source_actor_display_name}
                    />
                  ) : (
                    <MonoOrDash value={path.source_actor_id} />
                  )}
                </TableCell>
                <TableCell>
                  <MonoOrDash value={path.context_id} />
                </TableCell>
                <TableCell>{formatDateTime(path.expires_at)}</TableCell>
                <TableCell>
                  <ActiveCell active={path.active_now} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InspectorSection>
  );
}

function MembershipsSection({ memberships }: { memberships: readonly Membership[] }) {
  const active = memberships.filter((item) => item.active_now).length;
  return (
    <InspectorSection
      title="Memberships"
      aside={<ActiveOfBadge active={active} total={memberships.length} />}
    >
      {memberships.length === 0 ? (
        <SectionEmpty
          icon="companies"
          message="This actor belongs to no teams or collective actors."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Parent actor</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Context</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {memberships.map((membership) => (
              <TableRow key={membership.id}>
                <TableCell>
                  <RefLink
                    resource="actors"
                    id={membership.parent_actor_id}
                    label={membership.parent_actor_display_name ?? undefined}
                  />
                </TableCell>
                <TableCell>{humanize(membership.membership_type)}</TableCell>
                <TableCell>
                  <MonoOrDash value={membership.context_id} />
                </TableCell>
                <TableCell>{formatDateTime(membership.expires_at)}</TableCell>
                <TableCell>
                  <ActiveCell active={membership.active_now} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InspectorSection>
  );
}

function DelegationsSection({ delegations }: { delegations: readonly DelegationGrant[] }) {
  const active = delegations.filter((item) => item.active_now).length;
  return (
    <InspectorSection
      title="Delegations"
      aside={<ActiveOfBadge active={active} total={delegations.length} />}
    >
      {delegations.length === 0 ? (
        <SectionEmpty
          icon="transitionProposals"
          message="No actor has delegated authority to this actor."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Delegator</TableHead>
              <TableHead>Capability</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {delegations.map((grant) => (
              <TableRow key={grant.id}>
                <TableCell>
                  <RefLink
                    resource="actors"
                    id={grant.delegator_actor_id}
                    label={grant.delegator_display_name ?? undefined}
                  />
                </TableCell>
                <TableCell>
                  <MonoOrDash value={grant.capability} />
                </TableCell>
                <TableCell>
                  <MonoOrDash value={grant.resource_type} />
                </TableCell>
                <TableCell>{formatDateTime(grant.expires_at)}</TableCell>
                <TableCell>
                  <ActiveCell active={grant.active_now} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InspectorSection>
  );
}

function CredentialsSection({ grants }: { grants: readonly CredentialGrant[] }) {
  const active = grants.filter((item) => item.active_now).length;
  return (
    <InspectorSection
      title="Credentials"
      aside={<ActiveOfBadge active={active} total={grants.length} />}
    >
      {grants.length === 0 ? (
        <SectionEmpty
          icon="tool"
          message="This actor holds no tool credential grants."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tool</TableHead>
              <TableHead>Capability</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead>Scope hash</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grants.map((grant) => (
              <TableRow key={grant.id}>
                <TableCell>{humanize(grant.tool_name)}</TableCell>
                <TableCell>
                  <MonoOrDash value={grant.capability} />
                </TableCell>
                <TableCell>
                  {grant.allowed_scopes.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {grant.allowed_scopes.map((scope) => (
                        <Badge key={scope} variant="outline" className="font-mono text-xs">
                          {scope}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <MonoOrDash value={grant.scope_hash} />
                </TableCell>
                <TableCell>
                  <ActiveCell active={grant.active_now} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InspectorSection>
  );
}

function PolicyOverridesSection({ policies }: { policies: readonly PolicyOverride[] }) {
  return (
    <InspectorSection
      title="Policy Overrides"
      aside={
        <Badge variant="outline" className="font-mono">
          {policies.length}
        </Badge>
      }
    >
      {policies.length === 0 ? (
        <SectionEmpty
          icon="rules"
          message="No action-policy overrides apply to this actor's requested action."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Capability</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Approval mode</TableHead>
              <TableHead>Rank</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {policies.map((policy) => (
              <TableRow key={policy.id}>
                <TableCell>
                  <MonoOrDash value={policy.capability} />
                </TableCell>
                <TableCell>
                  <MonoOrDash value={policy.resource_type} />
                </TableCell>
                <TableCell>
                  <StatusBadge status={policy.risk_level} />
                </TableCell>
                <TableCell>
                  <StatusBadge status={policy.approval_mode} />
                </TableCell>
                <TableCell className="font-mono tabular-nums">{policy.policy_rank}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InspectorSection>
  );
}

function SectionEmpty({ icon, message }: { icon: React.ComponentProps<typeof Icon>["name"]; message: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
      <Icon name={icon} aria-hidden />
      {message}
    </div>
  );
}

function ActorPassportContent({
  packet,
  reload,
}: {
  packet: ActorPassportPacket;
  reload: () => void;
}) {
  const summary = packet.summary;
  /* When integrity warnings are present we keep the full passport rendered but
   * route it through the shared `redacted` banner (the doctrine: never hide the
   * underlying authority state — disclose the gaps). */
  const hasWarnings = packet.integrity_warnings.length > 0;
  const state: RenderStateKind = hasWarnings ? "redacted" : "normal";

  const body = (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Metric label="Identities" value={summary.identities} />
        <Metric label="Memberships" value={summary.memberships} />
        <Metric label="Role paths" value={summary.role_paths} />
        <Metric label="Delegations" value={summary.delegations} />
        <Metric label="Credentials" value={summary.credential_grants} />
      </div>

      <OverviewSection packet={packet} />
      <RuntimePrincipalSection packet={packet} />
      <IdentitiesSection identities={packet.identities} />
      <RolePathsSection rolePaths={packet.role_paths} />
      <MembershipsSection memberships={packet.memberships} />
      <DelegationsSection delegations={packet.delegation_grants} />
      <CredentialsSection grants={packet.credential_grants} />
      <PolicyOverridesSection policies={packet.policy_overrides} />
    </div>
  );

  return (
    <RenderState
      state={state}
      redactedMessage={
        hasWarnings
          ? `${packet.integrity_warnings.length} integrity warning${
              packet.integrity_warnings.length === 1 ? "" : "s"
            } were raised while assembling this passport. Some authority data may be incomplete.`
          : undefined
      }
      onRetry={reload}
    >
      {body}
      {hasWarnings ? (
        <div className="mt-4 rounded-md border border-border bg-muted/30 px-4 py-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Icon name="rules" aria-hidden />
            Integrity warnings
          </div>
          <ul className="list-inside list-disc text-sm text-muted-foreground">
            {packet.integrity_warnings.map((warning) => (
              <li key={warning} className="[overflow-wrap:anywhere]">
                {warning}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </RenderState>
  );
}

/**
 * Map an empty `surface_state` to a tailored RenderState empty config — an actor
 * with no identity / no authority is a real, expected projection, not an error.
 */
function emptyFor(packet: ActorPassportPacket): {
  empty: boolean;
  title: string;
  description: string;
} {
  switch (packet.surface_state) {
    case "no_identity":
      return {
        empty: true,
        title: "No identity linked",
        description: "This actor has no login or connector identity yet, so it cannot act.",
      };
    case "no_authority":
      return {
        empty: true,
        title: "No authority granted",
        description:
          "This actor has no role assignments, memberships, or delegations — it holds no authority.",
      };
    default:
      return { empty: false, title: "", description: "" };
  }
}

export function ActorPassportRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <ErrorState message="Missing actor id." />;
  return <ActorPassportPage actorId={id} />;
}

function ActorPassportPage({ actorId }: { actorId: string }) {
  const go = useGo();
  const { data, loading, error, forbidden, reload } = useActorPassportPacket(actorId);

  const empty = data ? emptyFor(data) : { empty: false, title: "", description: "" };

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Actor Passport"
          subtitle="Actor-centered authority surface: identities, memberships, role paths, delegations, credential grants, and the resolved runtime principal."
          icon="roles"
          actions={
            <Button variant="outline" onClick={() => go({ to: `/actors/${actorId}` })}>
              <Icon name="user" data-icon="inline-start" aria-hidden />
              Actor
            </Button>
          }
        />
        {loading ? <RenderState state="loading" loadingRows={6} /> : null}
        {!loading && forbidden ? (
          <RenderState
            state="forbidden"
            forbiddenReason="You do not have permission to view this actor's passport."
            forbiddenApprovalPath="an Access administrator"
          />
        ) : null}
        {!loading && !forbidden && error ? (
          <RenderState state="error" errorMessage={error} onRetry={reload} />
        ) : null}
        {!loading && !forbidden && !error && data && empty.empty ? (
          <RenderState
            state="empty"
            empty={{ icon: "roles", title: empty.title, hint: empty.description }}
          />
        ) : null}
        {!loading && !forbidden && !error && data && !empty.empty ? (
          <ActorPassportContent packet={data} reload={reload} />
        ) : null}
      </div>
    </div>
  );
}

export default ActorPassportRoute;
